<script>
  import { onMount, tick } from 'svelte';
  import { getApiUrl } from '../api.js';
  import { t } from '../i18nStore.js';

  let erdMarkup = '';
  let rawMarkup = ''; // Store original ERD diagram syntax from backend
  let allEntities = []; // List of all entity names found in rawMarkup
  let selectedEntities = new Set(); // Set of currently selected entities
  let container;
  let error = null;
  let loading = true;
  let showCode = false;

  // Function to load Mermaid library from local assets dynamically
  function loadMermaid() {
    return new Promise((resolve, reject) => {
      if (window.mermaid) {
        resolve(window.mermaid);
        return;
      }
      const script = document.createElement('script');
      script.src = '/mermaid.min.js';
      script.onload = () => resolve(window.mermaid);
      script.onerror = () => reject(new Error('Failed to load Mermaid.js library from local asset'));
      document.head.appendChild(script);
    });
  }

  // Parse entity names from a raw Mermaid ERD diagram string
  function getEntitiesList(markup) {
    if (!markup) return [];
    const regex = /^\s*([A-Za-z0-9_]+)\s*\{/gm;
    const entities = [];
    let match;
    while ((match = regex.exec(markup)) !== null) {
      entities.push(match[1]);
    }
    return [...new Set(entities)].sort();
  }

  // Filter raw Mermaid diagram string by checking if entities/relationships match selection set
  function filterMermaidMarkup(markup, selected) {
    if (!markup) return '';
    if (selected.size === 0) {
      return 'erDiagram';
    }

    const lines = markup.split('\n');
    const filteredLines = ['erDiagram'];
    
    let inEntityBlock = false;
    let currentEntity = '';
    let entityContent = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        filteredLines.push(line);
        continue;
      }

      // Check if starting an entity block
      const entityStartMatch = trimmed.match(/^([A-Za-z0-9_]+)\s*\{/);
      if (entityStartMatch) {
        inEntityBlock = true;
        currentEntity = entityStartMatch[1];
        entityContent = [line];
        continue;
      }

      if (inEntityBlock) {
        entityContent.push(line);
        if (trimmed === '}') {
          inEntityBlock = false;
          if (selected.has(currentEntity)) {
            filteredLines.push(...entityContent);
          }
        }
        continue;
      }

      // Relationship line: Entity1 ||--o{ Entity2 : "links"
      const relMatch = trimmed.match(/^([A-Za-z0-9_]+)\s+[^ ]+\s+([A-Za-z0-9_]+)/);
      if (relMatch) {
        const ent1 = relMatch[1];
        const ent2 = relMatch[2];
        if (selected.has(ent1) && selected.has(ent2)) {
          filteredLines.push(line);
        }
        continue;
      }

      if (trimmed !== 'erDiagram') {
        filteredLines.push(line);
      }
    }

    return filteredLines.join('\n');
  }

  async function fetchAndRender() {
    console.log("[ERDViewer] Starting fetchAndRender...");
    loading = true;
    error = null;
    try {
      const targetUrl = getApiUrl('/dev/erd');
      console.log("[ERDViewer] Fetching ERD markup from:", targetUrl);
      const response = await fetch(targetUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch ERD structure: ${response.statusText}`);
      }
      const data = await response.json();
      rawMarkup = data.mermaid;
      console.log("[ERDViewer] Fetched ERD raw markup length:", rawMarkup ? rawMarkup.length : 0);

      // Parse entities list and default select all
      allEntities = getEntitiesList(rawMarkup);
      selectedEntities = new Set(allEntities);

      // Stop loading state so container is mounted in the DOM
      loading = false;
      await tick();

      await renderFiltered();
    } catch (err) {
      console.error("[ERDViewer] Catch block caught error:", err);
      error = err.message;
      loading = false;
    } finally {
      console.log("[ERDViewer] fetchAndRender completed.");
    }
  }

  async function renderFiltered() {
    console.log("[ERDViewer] renderFiltered called...");
    if (!rawMarkup) return;

    error = null;
    erdMarkup = filterMermaidMarkup(rawMarkup, selectedEntities);

    try {
      const m = await loadMermaid();
      m.initialize({
        startOnLoad: false,
        theme: 'neutral',
        securityLevel: 'loose',
        flowchart: { useMaxWidth: true, htmlLabels: true }
      });

      if (container) {
        container.innerHTML = '';
        console.log("[ERDViewer] Invoking m.render('erd-svg-render', filteredMarkup)...");
        const renderResult = await m.render('erd-svg-render', erdMarkup);
        const { svg } = renderResult;
        container.innerHTML = svg;
        console.log("[ERDViewer] Filtered SVG inserted successfully.");
      } else {
        console.warn("[ERDViewer] Container element not found in DOM for filtered render.");
      }
    } catch (renderError) {
      console.error("[ERDViewer] Filtered render error:", renderError);
      error = `Mermaid Render Error: ${renderError.message || renderError}`;
    }
  }

  function toggleEntity(entity, isChecked) {
    if (isChecked) {
      selectedEntities.add(entity);
    } else {
      selectedEntities.delete(entity);
    }
    selectedEntities = selectedEntities; // Trigger reactivity
    renderFiltered();
  }

  function selectAllEntities() {
    selectedEntities = new Set(allEntities);
    renderFiltered();
  }

  function selectNoneEntities() {
    selectedEntities = new Set();
    renderFiltered();
  }

  onMount(() => {
    fetchAndRender();
  });

  function copyToClipboard() {
    navigator.clipboard.writeText(erdMarkup);
    alert($t('erd.copied_msg'));
  }
</script>

<div class="erd-viewer-container">
  <div class="header">
    <div>
      <h1>{$t('erd.title')}</h1>
      <p class="subtitle">{$t('erd.subtitle')}</p>
    </div>
    <div class="actions">
      <button class="btn btn-secondary" on:click={fetchAndRender} disabled={loading}>
        {$t('erd.btn_refresh')}
      </button>
      <button class="btn btn-primary" on:click={() => showCode = !showCode}>
        {showCode ? $t('erd.btn_view_diagram') : $t('erd.btn_view_code')}
      </button>
      {#if erdMarkup}
        <button class="btn btn-copy" on:click={copyToClipboard}>
          {$t('erd.btn_copy_code')}
        </button>
      {/if}
    </div>
  </div>

  {#if loading}
    <div class="card status-card">
      <div class="loader"></div>
      <p>{$t('erd.loading')}</p>
    </div>
  {:else}
    {#if error}
      <div class="card error-card">
        <h3>⚠️ Execution Error</h3>
        <p>{error}</p>
        <button class="btn btn-primary" on:click={fetchAndRender}>Retry</button>
      </div>
    {:else}
      {#if allEntities.length > 0}
        <div class="card filter-card">
          <div class="filter-header">
            <h3>🔍 {$t('erd.filter_entities')} ({selectedEntities.size} / {allEntities.length})</h3>
            <div class="filter-actions">
              <button class="btn btn-secondary btn-sm" on:click={selectAllEntities}>{$t('erd.select_all')}</button>
              <button class="btn btn-secondary btn-sm" on:click={selectNoneEntities}>{$t('erd.clear_all')}</button>
            </div>
          </div>
          <div class="entities-grid">
            {#each allEntities as entity}
              <label class="entity-checkbox-label">
                <input 
                  type="checkbox" 
                  checked={selectedEntities.has(entity)} 
                  on:change={(e) => toggleEntity(entity, e.target.checked)}
                />
                <span>{entity}</span>
              </label>
            {/each}
          </div>
        </div>
      {/if}

      {#if showCode}
        <div class="card code-card">
          <pre><code>{erdMarkup}</code></pre>
        </div>
      {:else}
        <div class="card diagram-card">
          <div bind:this={container} class="mermaid-target"></div>
        </div>
      {/if}
    {/if}
  {/if}
</div>

<style>
  .erd-viewer-container {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1.5rem;
    font-family: system-ui, -apple-system, sans-serif;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;
    gap: 1rem;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1.5rem;
  }

  h1 {
    font-size: 2.25rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0 0 0.5rem 0;
  }

  .subtitle {
    color: #4b5563;
    font-size: 1rem;
    margin: 0;
  }

  code {
    background: #f3f4f6;
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    font-family: monospace;
  }

  .actions {
    display: flex;
    gap: 0.75rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.2s ease;
  }

  .btn-primary {
    background-color: #2563eb;
    color: white;
  }
  .btn-primary:hover {
    background-color: #1d4ed8;
  }

  .btn-secondary {
    background-color: #f3f4f6;
    color: #374151;
    border-color: #d1d5db;
  }
  .btn-secondary:hover {
    background-color: #e5e7eb;
  }

  .btn-copy {
    background-color: #10b981;
    color: white;
  }
  .btn-copy:hover {
    background-color: #059669;
  }

  .card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #f3f4f6;
    padding: 2rem;
  }

  .status-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #4b5563;
  }

  .loader {
    border: 3px solid #f3f4f6;
    border-top: 3px solid #2563eb;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-card {
    text-align: center;
    border-color: #fecaca;
    background-color: #fef2f2;
    color: #991b1b;
  }

  .diagram-card {
    min-height: 600px;
    overflow: auto;
    background: #fafafa;
    border: 1px solid #e5e7eb;
  }

  .mermaid-target {
    padding: 2rem;
    display: block;
    width: max-content;
  }

  .mermaid-target :global(svg) {
    max-width: none !important;
    max-height: none !important;
  }

  .code-card {
    background-color: #1e1e2e;
    color: #cdd6f4;
    overflow-x: auto;
  }

  pre {
    margin: 0;
    font-family: 'Fira Code', monospace;
    font-size: 0.875rem;
  }

  .filter-card {
    margin-bottom: 1.5rem;
    padding: 1.5rem;
  }

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .filter-header h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #1e3a8a;
    font-weight: 600;
  }

  .filter-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-sm {
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;
    border-radius: 4px;
  }

  .entities-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.5rem 1rem;
    max-height: 200px;
    overflow-y: auto;
    padding: 0.5rem;
    border: 1px solid #f3f4f6;
    border-radius: 6px;
    background: #fafafa;
  }

  .entity-checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: #374151;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    transition: background 0.1s ease;
  }

  .entity-checkbox-label:hover {
    background: #f3f4f6;
  }

  .entity-checkbox-label input {
    cursor: pointer;
    margin: 0;
  }
</style>
